import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { loggedIn, loggedOut } from '@stillmandigital/common';
import { accountsResolver } from './accounts/accounts.resolver';
import { adminShellGuard } from './admin-shell.guard';
import { changePasswordGuard } from './auth/change-password.guard';
import { twoFactorAuthResolver } from './auth/two-factor-auth.resolver';
import { complianceShellGuard } from './compliance-shell.guard';
import { COMPLIANCE_CLIENT_REPORT_ROUTE_ID } from './compliance/client-report/compliance-client-report.store';
import { COMPLIANCE_LEADERBOARD_ROUTE_ID } from './compliance/leaderboard/compliance-leaderboard.store';
import { COMPLIANCE_TRANSFERS_HISTORY_ROUTE_ID } from './compliance/transfers-history/compliance-transfers-history.store';
import { houseLpResolver } from './house-lp/house-lp.resolver';
import { marketMakingPlatformsGuard } from './market-making/market-making-platforms.guard';

const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./shell/components/shell/shell.component').then(c => c.ShellComponent),
    canActivate: [loggedIn, adminShellGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'trading',
      },
      {
        path: 'trading',
        loadChildren: () => import('./trading/trading.module').then(module => module.TradingModule),
      },
      {
        path: 'analytics',
        loadChildren: () => import('./analytics/analytics.module').then(module => module.AnalyticsModule),
      },
      {
        path: 'reports',
        loadChildren: () => import('./reports/reports.module').then(module => module.ReportsModule),
      },
      {
        path: 'accounts',
        loadChildren: () => import('./accounts/accounts.module').then(module => module.AccountsModule),
        resolve: {
          dataLoad: accountsResolver,
        },
      },
      {
        path: 'transfers',
        loadChildren: () => import('./transfers/transfers.module').then(module => module.TransfersModule),
      },
      {
        path: 'internal-lps',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'house-lp',
          },
          {
            path: 'house-lp',
            loadChildren: () => import('./house-lp/house-lp.module').then(module => module.HouseLpModule),
            resolve: {
              dataLoad: houseLpResolver,
            },
          },
          {
            path: 'b-book-lp',
            loadChildren: () => import('./b-book-lp/b-book-lp.module').then(module => module.BBookLpModule),
          },
          {
            path: 'main-book-lp',
            loadChildren: () => import('./main-book-lp/main-book-lp.module').then(module => module.MainBookLpModule),
          },
          {
            path: 'principal-risk-lp',
            loadChildren: () =>
              import('./principal-risk-lp/principal-risk-lp.module').then(module => module.PrincipalRiskLpModule),
          },
          {
            path: 'synthetic-lp',
            loadChildren: () => import('./synthetic-lp/synthetic-lp.module').then(module => module.SyntheticLpModule),
          },
        ],
      },

      {
        path: 'users',
        loadChildren: () => import('./users/users.module').then(module => module.UsersModule),
      },
      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then(module => module.SettingsModule),
      },
      {
        path: 'fireblocks',
        loadChildren: () => import('./fireblocks/fireblocks.module').then(module => module.FireblocksModule),
      },
      {
        path: 'logs',
        loadChildren: () => import('./logs/logs.module').then(module => module.LogsModule),
      },
      {
        path: 'accounting',
        loadChildren: () => import('./accounting/accounting.module').then(module => module.AccountingModule),
      },
      {
        path: 'leaderboard',
        loadChildren: () => import('./leaderboard/leaderboard.module').then(module => module.LeaderboardModule),
      },
      {
        path: 'market-making',
        canMatch: [marketMakingPlatformsGuard],
        loadChildren: () => import('./market-making/market-making.module').then(module => module.MarketMakingModule),
      },
    ],
  },
  {
    path: 'compliance',
    loadComponent: () =>
      import('./shell/components/compliance-shell/compliance-shell.component').then(c => c.ComplianceShellComponent),
    canActivate: [loggedIn, complianceShellGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'client-report',
      },
      {
        path: 'client-report',
        loadComponent: () =>
          import('./compliance/client-report/compliance-client-report.component').then(
            c => c.ComplianceClientReportComponent,
          ),
        data: { [COMPLIANCE_CLIENT_REPORT_ROUTE_ID]: true },
      },
      {
        path: 'transfers-history',
        loadComponent: () =>
          import('./compliance/transfers-history/compliance-transfers-history.component').then(
            c => c.ComplianceTransfersHistoryComponent,
          ),
        data: { [COMPLIANCE_TRANSFERS_HISTORY_ROUTE_ID]: true },
      },
      {
        path: 'leaderboard',
        loadComponent: () =>
          import('./compliance/leaderboard/compliance-leaderboard.component').then(
            c => c.ComplianceLeaderboardComponent,
          ),
        data: { [COMPLIANCE_LEADERBOARD_ROUTE_ID]: true },
      },
    ],
  },
  {
    path: 'login',
    canActivate: [loggedOut],
    loadComponent: () => import('./auth/components/login-page/login-page.component').then(c => c.LoginPageComponent),
  },
  {
    path: 'change-password',
    canActivate: [loggedIn, changePasswordGuard],
    loadComponent: () =>
      import('./auth/components/change-password/change-password.component').then(c => c.ChangePasswordComponent),
  },
  {
    path: 'auth-setup',
    canActivate: [loggedIn],
    loadComponent: () =>
      import('./auth/components/two-factor-auth/two-factor-auth.component').then(c => c.TwoFactorAuthComponent),
    resolve: {
      dataLoad: twoFactorAuthResolver,
    },
  },
  {
    path: '**',
    loadComponent: () =>
      import('./error-handling/components/page-not-found/page-not-found.component').then(c => c.PageNotFoundComponent),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      paramsInheritanceStrategy: 'always',
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
